import {
  AgentConfig,
  AgentConfigGlobalContexts,
  AgentConfigStep,
  AgentConfigStepAction,
  AgentConfigStepInputs,
  AgentConfigStepLabelEnum,
  AgentConfigStepRouteOutputTypeEnum,
  AgentConfigStepRoutes
} from '@/types/agent';

import { ValidationErrorMap } from '../util/validation';


export const AgentEditActionType = {
  CONFIG_UPDATE_CONTEXT: 'CONFIG_UPDATE_CONTEXT',
  LOAD: 'LOAD',
  ROLLBACK: 'ROLLBACK',
  STEP_ADD: 'STEP_ADD',
  STEP_DELETE: 'STEP_DELETE',
  STEP_MOVE: 'STEP_MOVE',
  STEP_UPDATE_ACTION: 'STEP_UPDATE_ACTION',
  STEP_UPDATE_DESCRIPTION: 'STEP_UPDATE_DESCRIPTION',
  STEP_UPDATE_FIRST_ROUTE: 'STEP_UPDATE_FIRST_ROUTE',
  STEP_UPDATE_INPUTS: 'STEP_UPDATE_INPUTS',
  STEP_UPDATE_NAME: 'STEP_UPDATE_NAME',
  STEP_UPDATE_ROUTES: 'STEP_UPDATE_ROUTES',
  VALIDATION_ERRORS_CLEAR: 'VALIDATION_ERRORS_CLEAR',
  VALIDATION_ERRORS_SET: 'VALIDATION_ERRORS_SET'
} as const;

export type AgentEditAction =
  | { type: typeof AgentEditActionType.CONFIG_UPDATE_CONTEXT; context: AgentConfigGlobalContexts }
  | { type: typeof AgentEditActionType.LOAD; config: AgentConfig }
  | { type: typeof AgentEditActionType.ROLLBACK; config: AgentConfig }
  | { type: typeof AgentEditActionType.STEP_ADD; step: AgentConfigStep }
  | { type: typeof AgentEditActionType.STEP_DELETE; stepId: string }
  | { type: typeof AgentEditActionType.STEP_MOVE; stepId: string; position: { x: number; y: number } }
  | { type: typeof AgentEditActionType.STEP_UPDATE_ACTION; stepId: string; action: AgentConfigStepAction }
  | { type: typeof AgentEditActionType.STEP_UPDATE_DESCRIPTION; stepId: string; value: string }
  | { type: typeof AgentEditActionType.STEP_UPDATE_FIRST_ROUTE; stepId: string; value: string }
  | { type: typeof AgentEditActionType.STEP_UPDATE_INPUTS; stepId: string; inputs: AgentConfigStepInputs }
  | { type: typeof AgentEditActionType.STEP_UPDATE_NAME; stepId: string; value: string }
  | { type: typeof AgentEditActionType.STEP_UPDATE_ROUTES; stepId: string; routes: AgentConfigStepRoutes }
  | { type: typeof AgentEditActionType.VALIDATION_ERRORS_CLEAR }
  | { type: typeof AgentEditActionType.VALIDATION_ERRORS_SET; errors: ValidationErrorMap };

// Recalculates inputs for all steps based on current routes.
// Each step's inputs are derived from all outputs pointing to it.
// START node inputs are preserved (user-defined, not route-derived).
const recalculateAllInputs = ( steps: AgentConfigStep[] ): AgentConfigStep[] => {
  return steps.map( ( step ) => {
    // START node has user-defined inputs, don't recalculate
    if ( step.id === AgentConfigStepLabelEnum.START ) {
      return step;
    }

    const inputs: Record<string, { type: string }> = {};

    // Collect outputs from all steps that route to this step
    steps.forEach( ( sourceStep ) => {
      Object.values( sourceStep.routes ).forEach( ( route ) => {
        if ( route.step === step.id ) {
          Object.entries( route.outputs || {} ).forEach( ( [ outputName, outputConfig ] ) => {
            let type = 'string'; // fallback default

            if ( outputConfig.type === AgentConfigStepRouteOutputTypeEnum.FROM_INPUT ) {
              // Infer type from source step's input
              type = sourceStep.inputs?.[outputConfig.key]?.type || 'string';
            } else if ( outputConfig.type === AgentConfigStepRouteOutputTypeEnum.FROM_ACTION ) {
              // Infer type from known action output types
              if ( outputConfig.key === 'merged_conversation' ) {
                type = 'list';
              } else if ( outputConfig.key === 'response' || outputConfig.key === 'route' ) {
                type = 'string';
              }
              // Tool parameters default to 'string' (could be enhanced to check tool schema)
            }

            inputs[outputName] = { type };
          } );
        }
      } );
    } );

    // Disconnected nodes get empty inputs {} (like global context empty state)
    return { ...step, inputs };
  } );
};

export function agentEditReducer(
  state: AgentConfig | null,
  action: AgentEditAction
): AgentConfig | null {
  if ( action.type === AgentEditActionType.LOAD || action.type === AgentEditActionType.ROLLBACK ) return action.config;
  if ( !state ) return null;

  switch ( action.type ) {
    case AgentEditActionType.CONFIG_UPDATE_CONTEXT: {
      return { ...state, context: action.context };
    }

    case AgentEditActionType.STEP_ADD:
      return { ...state, steps: [ ...state.steps, action.step ] };

    case AgentEditActionType.STEP_DELETE: {
      const nextSteps = state.steps
        .filter( ( s ) => s.id !== action.stepId )
        .map( ( s ) => ( {
          ...s,
          routes: Object.fromEntries(
            Object.entries( s.routes ).map( ( [ key, route ] ) => [
              key,
              route.step === action.stepId ? { ...route, step: undefined } : route
            ] )
          )
        } ) );
      return { ...state, steps: recalculateAllInputs( nextSteps ) };
    }

    case AgentEditActionType.STEP_MOVE: {
      const nextSteps = state.steps.map( ( s ) => (
        s.id === action.stepId ? { ...s, position: action.position } : s
      ) );
      return { ...state, steps: nextSteps };
    }

    case AgentEditActionType.STEP_UPDATE_ACTION: {
      const stepIndex = state.steps.findIndex( ( s ) => s.id === action.stepId );
      if ( stepIndex === -1 ) return state;

      const step = state.steps[stepIndex];
      const nextStep = { ...step, action: action.action, routes: { ...step.routes } };

      const routeKeys = new Set( Object.keys( nextStep.routes ) );
      const usedRoutes = new Set<string>( [ 'default' ] );

      if ( action.action.mcp_servers ) {
        for ( const mcpClient of action.action.mcp_servers ) {
          usedRoutes.add( mcpClient.name );
          if ( !routeKeys.has( mcpClient.name ) ) {
            nextStep.routes[mcpClient.name] = { step: AgentConfigStepLabelEnum.END, outputs: {} };
            routeKeys.add( mcpClient.name );
          }
        }
      }

      if ( action.action.tools ) {
        for ( const tool of action.action.tools ) {
          usedRoutes.add( tool.function.name );
          if ( !routeKeys.has( tool.function.name ) ) {
            nextStep.routes[tool.function.name] = { step: AgentConfigStepLabelEnum.END, outputs: {} };
            routeKeys.add( tool.function.name );
          }
        }
      }

      const unusedRoutes = routeKeys.difference( usedRoutes );
      unusedRoutes.forEach( ( routeName ) => delete nextStep.routes[routeName] );

      const nextSteps = [ ...state.steps ];
      nextSteps[stepIndex] = nextStep;
      return { ...state, steps: recalculateAllInputs( nextSteps ) };
    }

    case AgentEditActionType.STEP_UPDATE_DESCRIPTION: {
      const nextSteps = state.steps.map( ( s ) => (
        s.id === action.stepId ? { ...s, description: action.value } : s
      ) );
      return { ...state, steps: nextSteps };
    }

    case AgentEditActionType.STEP_UPDATE_FIRST_ROUTE: {
      const stepIndex = state.steps.findIndex( ( s ) => s.id === action.stepId );
      if ( stepIndex === -1 ) return state;

      const step = state.steps[stepIndex];

      const nextStep = {
        ...step,
        routes: {
          ...step.routes,
          default: {
            ...step.routes.default,
            step: action.value
          }
        }
      };

      const nextSteps = [ ...state.steps ];
      nextSteps[stepIndex] = nextStep;

      return { ...state, steps: recalculateAllInputs( nextSteps ) };
    }

    case AgentEditActionType.STEP_UPDATE_INPUTS: {
      const stepIndex = state.steps.findIndex( ( s ) => s.id === action.stepId );
      if ( stepIndex === -1 ) return state;

      const step = state.steps[stepIndex];
      const nextStep = { ...step, inputs: action.inputs };

      // Special case: START node inputs should map to outputs
      if ( step.id === AgentConfigStepLabelEnum.START ) {
        const updatedRoutes = { ...step.routes };
        Object.keys( updatedRoutes ).forEach( ( routeKey ) => {
          const outputs: Record<string, any> = {};
          Object.keys( action.inputs ).forEach( ( inputKey ) => {
            outputs[inputKey] = {
              type: AgentConfigStepRouteOutputTypeEnum.FROM_INPUT,
              key: inputKey
            };
          } );
          updatedRoutes[routeKey] = { ...updatedRoutes[routeKey], outputs };
        } );
        nextStep.routes = updatedRoutes;
      }

      const nextSteps = [ ...state.steps ];
      nextSteps[stepIndex] = nextStep;

      // If START node, propagate outputs downstream
      if ( step.id === AgentConfigStepLabelEnum.START ) {
        return { ...state, steps: recalculateAllInputs( nextSteps ) };
      }

      return { ...state, steps: nextSteps };
    }

    case AgentEditActionType.STEP_UPDATE_NAME: {
      const oldId = action.stepId;
      const newId = action.value;
      const nextSteps = state.steps.map( ( step ) => {
        const updated = step.id === oldId ? { ...step, id: newId } : { ...step };
        const updatedRoutes = Object.fromEntries(
          Object.entries( updated.routes ).map( ( [ key, route ] ) => [
            key,
            route.step === oldId ? { ...route, step: newId } : route
          ] )
        );
        return { ...updated, routes: updatedRoutes };
      } );
      return { ...state, steps: nextSteps };
    }

    case AgentEditActionType.STEP_UPDATE_ROUTES: {
      const stepIndex = state.steps.findIndex( ( s ) => s.id === action.stepId );
      if ( stepIndex === -1 ) return state;

      const nextSteps = [ ...state.steps ];
      nextSteps[stepIndex] = { ...nextSteps[stepIndex], routes: action.routes };
      return { ...state, steps: recalculateAllInputs( nextSteps ) };
    }

    default:
      return state;
  }
}
